import { useEffect, useRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import Image from '@tiptap/extension-image';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Columns3,
  ImagePlus,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Palette,
  Redo2,
  Rows3,
  Strikethrough,
  Table2,
  Trash2,
  Undo2,
  Underline as UnderlineIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import { uploadToStorage } from '../../../database/storage';
import { cn } from '../../../lib/utils';
import { normalizeLegacyRulesHtml } from '../../Public/tournament/tournamentHelpers';

const COLORS = ['#f97316', '#ef4444', '#eab308', '#22c55e', '#38bdf8', '#a78bfa', '#111827'];

const ToolbarButton = ({ onClick, active, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    className={cn(
      'inline-flex h-8 w-8 items-center justify-center rounded text-muted-foreground transition-colors',
      'hover:bg-secondary/80 hover:text-foreground',
      'disabled:pointer-events-none disabled:opacity-40',
      active && 'bg-secondary text-foreground'
    )}
  >
    {children}
  </button>
);

const TournamentRulesEditor = ({ value, onChange, placeholder }) => {
  const fileInputRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Placeholder.configure({
        placeholder: placeholder || 'Write the tournament rules…',
        emptyEditorClass:
          'before:content-[attr(data-placeholder)] before:text-muted-foreground before:float-left before:pointer-events-none',
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-heat underline underline-offset-2' },
      }),
      TextStyle,
      Color.configure({ types: ['textStyle'] }),
      TextAlign.configure({ types: ['heading', 'paragraph', 'blockquote', 'listItem'] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Image.configure({ inline: false, allowBase64: false }),
    ],
    content: normalizeLegacyRulesHtml(value || ''),
    onUpdate: ({ editor: currentEditor }) => {
      const html = currentEditor.getHTML();
      onChange?.(html === '<p></p>' ? '' : html);
    },
  });

  // Settings is normally mounted once, but this also keeps the editor in sync
  // when a parent reopens it with a freshly loaded legacy Quill document.
  useEffect(() => {
    if (!editor || editor.isFocused || typeof value !== 'string') return;
    const normalizedValue = normalizeLegacyRulesHtml(value);
    if (normalizedValue !== editor.getHTML() && normalizedValue !== '') {
      editor.commands.setContent(normalizedValue, { emitUpdate: false });
    }
    if (value === '' && editor.getHTML() !== '<p></p>') {
      editor.commands.clearContent({ emitUpdate: false });
    }
  }, [editor, value]);

  if (!editor) return null;

  const addLink = () => {
    const previous = editor.getAttributes('link').href;
    const url = window.prompt('Link URL', previous || 'https://');
    if (url === null) return;
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  const addImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const url = await uploadToStorage(file, 'tournament-rules');
      editor.chain().focus().setImage({ src: url, alt: file.name }).run();
    } catch {
      toast.error('Image upload failed.');
    }
  };

  const setBlockType = (event) => {
    const type = event.target.value;
    const chain = editor.chain().focus();
    if (type === 'paragraph') chain.setParagraph().run();
    else chain.toggleHeading({ level: Number(type) }).run();
  };

  return (
    <div className="overflow-hidden rounded-md border border-border/60 bg-background/40">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border/60 bg-background/60 px-2 py-1.5">
        <select
          value={
            editor.isActive('heading', { level: 1 })
              ? '1'
              : editor.isActive('heading', { level: 2 })
                ? '2'
                : editor.isActive('heading', { level: 3 })
                  ? '3'
                  : 'paragraph'
          }
          onChange={setBlockType}
          className="h-8 rounded border border-border/60 bg-background px-2 text-xs text-foreground"
          aria-label="Text style"
        >
          <option value="paragraph">Paragraph</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
        </select>

        <span className="mx-1 h-5 w-px bg-border/60" />
        <ToolbarButton
          title="Bold"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Italic"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Underline"
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Strikethrough"
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Inline code"
          active={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}
        >
          <Code className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Bullet list"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Numbered list"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Link" active={editor.isActive('link')} onClick={addLink}>
          <LinkIcon className="h-3.5 w-3.5" />
        </ToolbarButton>

        <span className="mx-1 h-5 w-px bg-border/60" />
        <ToolbarButton
          title="Align left"
          active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
        >
          <AlignLeft className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Align center"
          active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
        >
          <AlignCenter className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Align right"
          active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
        >
          <AlignRight className="h-3.5 w-3.5" />
        </ToolbarButton>

        <label
          className="relative inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-secondary/80 hover:text-foreground"
          title="Text color"
        >
          <Palette className="h-3.5 w-3.5" />
          <input
            type="color"
            value={editor.getAttributes('textStyle').color || '#111827'}
            onChange={(event) => editor.chain().focus().setColor(event.target.value).run()}
            className="absolute inset-0 cursor-pointer opacity-0"
            aria-label="Text color"
          />
        </label>
        <div className="hidden items-center gap-0.5 sm:flex" aria-label="Text color presets">
          {COLORS.map((color) => (
            <button
              type="button"
              key={color}
              title={`Text color ${color}`}
              aria-label={`Text color ${color}`}
              onClick={() => editor.chain().focus().setColor(color).run()}
              className="h-4 w-4 rounded-full border border-border/70"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>

        <span className="mx-1 h-5 w-px bg-border/60" />
        <ToolbarButton
          title="Insert table"
          onClick={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          }
        >
          <Table2 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Add table row"
          disabled={!editor.isActive('table')}
          onClick={() => editor.chain().focus().addRowAfter().run()}
        >
          <Rows3 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Delete table row"
          disabled={!editor.isActive('table')}
          onClick={() => editor.chain().focus().deleteRow().run()}
        >
          <Rows3 className="h-3.5 w-3.5 text-destructive" />
        </ToolbarButton>
        <ToolbarButton
          title="Add table column"
          disabled={!editor.isActive('table')}
          onClick={() => editor.chain().focus().addColumnAfter().run()}
        >
          <Columns3 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Delete table column"
          disabled={!editor.isActive('table')}
          onClick={() => editor.chain().focus().deleteColumn().run()}
        >
          <Columns3 className="h-3.5 w-3.5 text-destructive" />
        </ToolbarButton>
        <ToolbarButton
          title="Delete table"
          disabled={!editor.isActive('table')}
          onClick={() => editor.chain().focus().deleteTable().run()}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton title="Upload image" onClick={() => fileInputRef.current?.click()}>
          <ImagePlus className="h-3.5 w-3.5" />
        </ToolbarButton>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={addImage}
          className="hidden"
        />

        <span className="mx-1 h-5 w-px bg-border/60" />
        <ToolbarButton
          title="Undo"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          <Undo2 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Redo"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          <Redo2 className="h-3.5 w-3.5" />
        </ToolbarButton>
      </div>
      <EditorContent
        editor={editor}
        className="tiptap tournament-rules-editor max-w-none px-4 py-3"
      />
    </div>
  );
};

export default TournamentRulesEditor;
